/**
 * Course learning path (local-first): lessons are traversed as nodes on a
 * winding path (Duolingo-style, Aula identity), with the student's XP/streak
 * and resume via `last_block_id`. Opening a lesson launches the
 * `LessonPlayer`. Everything comes from PowerSync (offline).
 */
import { type Component, createMemo, createSignal, For, type JSX, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Award, Check, Flame, Play, Star } from "@fleet/icons";
import type { Lesson } from "@aula/powersync";
import { isCourseCompleted } from "@aula/shared";
import { Link } from "@tanstack/solid-router";
import { Skeleton } from "@fleet/ui";
import { Mascot } from "@/components/ui/mascot";
import { useI18n } from "@/providers/i18n-provider";
import {
  useCourseCertificate,
  useCourseProgress,
  useCourseTree,
  useLearnerStats,
  useLessonBlocks,
} from "../../hooks/use-course-data";
import { LessonPlayer } from "./lesson-player";

export const CourseLearnView: Component<{ courseId: string }> = (props) => {
  const { t } = useI18n();
  const courseId = () => props.courseId;
  const { course, units, lessons } = useCourseTree(courseId);
  const progress = useCourseProgress(courseId);
  const stats = useLearnerStats();
  const [activeLessonId, setActiveLessonId] = createSignal<string | null>(null);

  const progressByLesson = createMemo(() => {
    const map = new Map<
      string,
      { status: string; lastBlockId: string | null; id: string; score: number }
    >();
    for (const p of progress.data ?? []) {
      map.set(p.lesson_id ?? "", {
        status: p.status ?? "in_progress",
        lastBlockId: p.last_block_id ?? null,
        id: p.id,
        score: p.score ?? 0,
      });
    }
    return map;
  });

  const lessonsByUnit = createMemo(() => {
    const map = new Map<string, Lesson[]>();
    for (const l of lessons.data ?? []) {
      const arr = map.get(l.unit_id ?? "") ?? [];
      arr.push(l);
      map.set(l.unit_id ?? "", arr);
    }
    return map;
  });

  const totalLessons = () => lessons.data?.length ?? 0;
  const completedCount = () => (progress.data ?? []).filter((p) => p.status === "completed").length;

  // COMPLETE course (pure mirror of the server criterion: all lessons
  // passed) → surfaces the server-issued certificate (arrives via sync).
  const courseDone = createMemo(() =>
    isCourseCompleted(
      (lessons.data ?? []).map((l) => ({ id: l.id, deleted_at: l.deleted_at })),
      (progress.data ?? []).map((p) => ({
        lesson_id: p.lesson_id,
        status: p.status,
        score: p.score,
      })),
    ),
  );
  const certificate = useCourseCertificate(courseId);

  // Index of the first uncompleted lesson → "current" node (highlighted).
  const currentLessonId = createMemo(() => {
    for (const l of lessons.data ?? []) {
      if (progressByLesson().get(l.id)?.status !== "completed") {
        return l.id;
      }
    }
    return null;
  });

  const activeLesson = createMemo(
    () => (lessons.data ?? []).find((l) => l.id === activeLessonId()) ?? null,
  );

  return (
    <Show
      when={!activeLessonId()}
      fallback={
        <div class="p-4 md:p-6">
          <Show when={activeLesson()}>
            {(l) => (
              <LessonPlayerLoader
                courseId={props.courseId}
                lesson={l()}
                progressId={progressByLesson().get(l().id)?.id}
                resumeBlockId={progressByLesson().get(l().id)?.lastBlockId ?? undefined}
                courseCompleted={completedCount() + 1 >= totalLessons()}
                onExit={() => setActiveLessonId(null)}
              />
            )}
          </Show>
        </div>
      }
    >
      <div class="mx-auto max-w-2xl space-y-6 p-4 md:p-6">
        <Link
          to="/learn"
          class="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-accent"
        >
          ← {t(msg`My courses`)}
        </Link>

        {/* Cabecera del curso con progreso global */}
        <Show when={!course.isLoading} fallback={<Skeleton class="h-28 w-full rounded-3xl" />}>
          <div class="aula-mesh rounded-3xl border border-border p-5 md:p-6">
            <h1
              class="font-display text-2xl font-extrabold tracking-tight"
              data-testid="learn-course-title"
            >
              {course.data?.title ?? t(msg`Course`)}
            </h1>
            <div class="mt-4 flex flex-wrap items-center gap-3">
              <HeaderStat
                icon={<Star class="h-4 w-4" />}
                value={stats.data?.total_xp ?? 0}
                label="XP"
                testid="learn-xp"
                cls="bg-joy-sun/20 text-joy-sun-foreground"
              />
              <HeaderStat
                icon={<Flame class="h-4 w-4" />}
                value={stats.data?.current_streak ?? 0}
                label={t(msg`streak`)}
                testid="learn-streak"
                cls="bg-joy-flame/15 text-joy-flame"
              />
              <HeaderStat
                icon={<Check class="h-4 w-4" />}
                value={`${completedCount()}/${totalLessons()}`}
                label={t(msg`lessons`)}
                testid="learn-completed"
                cls="bg-accent/12 text-accent"
              />
            </div>
            {/* Barra de progreso del curso */}
            <div class="mt-4 h-2.5 overflow-hidden rounded-full bg-muted/60">
              <div
                class="h-full rounded-full bg-gradient-to-r from-accent to-joy-berry transition-all duration-500"
                style={{
                  width: `${totalLessons() ? (completedCount() / totalLessons()) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
        </Show>

        {/* Certificado: el curso está completo → enlaza al certificado emitido
            por el servidor (llega por el stream my_certificates). */}
        <Show when={courseDone()}>
          <div
            class="flex flex-wrap items-center justify-between gap-3 rounded-3xl border-2 border-joy-sun/50 bg-joy-sun/10 p-5"
            data-testid="certificate-section"
          >
            <div class="flex items-center gap-3">
              <span class="flex h-11 w-11 items-center justify-center rounded-full bg-joy-sun/25 text-joy-sun-foreground">
                <Award class="h-6 w-6" />
              </span>
              <div>
                <p class="font-display font-bold">{t(msg`Course completed!`)}</p>
                <p class="text-xs text-muted-foreground">
                  <Show
                    when={certificate.data}
                    fallback={t(msg`Your certificate is being issued…`)}
                  >
                    {t(msg`Your certificate is ready.`)}
                  </Show>
                </p>
              </div>
            </div>
            <Show when={certificate.data}>
              {(cert) => (
                <Link
                  to="/certificates/$certId"
                  params={{ certId: cert().id }}
                  data-testid="certificate-link"
                  class="rounded-2xl bg-accent px-5 py-2.5 text-sm font-bold text-accent-foreground shadow-md shadow-accent/25 transition-transform hover:scale-105 active:scale-95"
                >
                  {t(msg`View certificate`)}
                </Link>
              )}
            </Show>
          </div>
        </Show>

        {/* La senda: unidades con nodos-lección serpenteantes */}
        <div class="space-y-8" data-testid="learn-syllabus">
          <For each={units.data ?? []}>
            {(u) => (
              <section>
                <div class="mb-3 flex items-center gap-2">
                  <span class="h-6 w-1.5 rounded-full bg-accent" />
                  <h3 class="font-display text-lg font-bold">{u.title}</h3>
                </div>
                <ol class="relative flex flex-col items-center gap-1">
                  <For each={lessonsByUnit().get(u.id) ?? []}>
                    {(l, i) => {
                      const p = () => progressByLesson().get(l.id);
                      const done = () => p()?.status === "completed";
                      const current = () => currentLessonId() === l.id;
                      // Serpentine path: alternates left/center/right.
                      const shift = () =>
                        ["translate-x-0", "translate-x-14", "-translate-x-14"][i() % 3];
                      return (
                        <li class={`flex flex-col items-center ${shift()}`}>
                          <button
                            type="button"
                            data-testid="learn-lesson"
                            data-lesson-done={done() ? "1" : "0"}
                            onClick={() => setActiveLessonId(l.id)}
                            class="group flex flex-col items-center gap-1.5"
                            aria-label={l.title ?? t(msg`Lesson`)}
                          >
                            <span
                              class="relative flex h-16 w-16 items-center justify-center rounded-full border-4 shadow-md transition-transform group-hover:scale-105 group-active:scale-95"
                              classList={{
                                "border-success bg-success text-success-foreground": done(),
                                "border-accent bg-accent text-accent-foreground shadow-accent/30":
                                  !done() && current(),
                                "border-border bg-surface text-muted-foreground":
                                  !done() && !current(),
                              }}
                            >
                              <Show
                                when={done()}
                                fallback={<Play class="h-6 w-6 translate-x-0.5" />}
                              >
                                <Check class="h-7 w-7" stroke-width={3} />
                              </Show>
                              <Show when={current()}>
                                <span class="absolute -inset-1.5 -z-10 animate-ping rounded-full bg-accent/40" />
                              </Show>
                            </span>
                            <span
                              class="max-w-[9rem] text-center text-xs font-semibold leading-tight"
                              classList={{ "text-muted-foreground": !done() && !current() }}
                            >
                              {l.title}
                            </span>
                            <span class="inline-flex items-center gap-1 text-[11px] font-bold text-joy-sun-foreground">
                              <Star class="h-3 w-3 fill-current text-joy-sun" /> {l.xp_reward} XP
                            </span>
                          </button>
                        </li>
                      );
                    }}
                  </For>
                </ol>
              </section>
            )}
          </For>
          <Show when={!course.isLoading && totalLessons() === 0}>
            <div class="flex flex-col items-center gap-3 rounded-3xl border-2 border-dashed border-border p-12 text-center text-muted-foreground">
              <Mascot mood="sleep" class="h-20 w-20" />
              <p>{t(msg`This course has no synced lessons yet.`)}</p>
            </div>
          </Show>
        </div>
      </div>
    </Show>
  );
};

/** Loads the active lesson's blocks and mounts the player. */
const LessonPlayerLoader: Component<{
  courseId: string;
  lesson: Lesson;
  progressId?: string;
  resumeBlockId?: string;
  courseCompleted: boolean;
  onExit: () => void;
}> = (props) => {
  const { t } = useI18n();
  const blocks = useLessonBlocks(() => props.lesson.id);
  return (
    <Show
      when={!blocks.isLoading}
      fallback={<Skeleton class="mx-auto h-[60vh] max-w-2xl rounded-3xl" />}
    >
      <LessonPlayer
        courseId={props.courseId}
        lessonId={props.lesson.id}
        lessonTitle={props.lesson.title ?? t(msg`Lesson`)}
        xpReward={props.lesson.xp_reward ?? 10}
        blocks={blocks.data ?? []}
        progressId={props.progressId}
        resumeBlockId={props.resumeBlockId}
        courseCompleted={props.courseCompleted}
        onExit={props.onExit}
      />
    </Show>
  );
};

const HeaderStat: Component<{
  icon: JSX.Element;
  label: string;
  value: JSX.Element;
  testid: string;
  cls: string;
}> = (props) => (
  <div
    class={`flex items-center gap-2 rounded-2xl px-3.5 py-1.5 ${props.cls}`}
    data-testid={props.testid}
  >
    <span class="opacity-90">{props.icon}</span>
    <span class="font-display text-lg font-extrabold leading-none">{props.value}</span>
    <span class="text-xs font-medium opacity-80">{props.label}</span>
  </div>
);
